import React, { useMemo, useState, ReactNode, useEffect } from "react";
import {
  Accordion,
  MainImageBackground,
  QrCard,
  Separator,
  SuccessMessage,
  TaxRefund,
  WarningBanner,
} from "src/components";

import styles from "./styles.module.scss";
import { UIButton, UICard, UIText } from "src/components/uIKit";
import { useAppDispatch, useAppSelector } from "src/hooks/useRedux";
import { images } from "src/assets/images/images";
import { ANDROID_URL, FULL_URL, GLOBAL_BLUE_THIRD_PARTY_LINK, IOS_URL } from "src/utils/consts";
import classNames from "classnames";

import {
  API_RESULT,
  COLLECT_CARD_INSTRUCTION,
  POPUP_TYPE,
  STATE,
  TEXT_TAGS,
} from "../../utils/enums";
import {
  CreditCardStatus,
  TAX_REFUND_CONDITION,
  UpdateOnboardingAnalytics,
} from "src/utils/types";
import { __DEV__, getTaxRefundCondition, logPfsAnalyticsEvent } from "src/utils/utils";
import { setPopupInfo } from "src/redux/slices/popupSlice/popupSlice";
import ScreenReaderText from "src/components/accessibilityKit/screenReaderText/ScreenReaderText";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useLogAnalytics } from "src/hooks/useLogAnalytics";

const isAndroid = navigator.userAgent.includes("Android");
const isIOS = navigator.userAgent.includes("iPhone");


const qrCodeUrl = isAndroid ? ANDROID_URL : IOS_URL;

const SummaryScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  useLogAnalytics({ summary: true });
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessageText, setErrorMessageText] = useState("");
  const [errorMessageSubtitle, setErrorMessageSubtitle] = useState("");

  const { strings, thirdSectionValid, fXEligibilityData } = useAppSelector(
    (state) => state.app
  );
  const { taxRefundInfoRes, getCCInfoRes, createTaxRefundMemberResStatus, createTaxRefundMemberError } = useAppSelector(
    (state) => state.user
  );


  useEffect(() => {
    __DEV__ && console.log("createTaxRefundMemberResStatus", createTaxRefundMemberResStatus);
    if (createTaxRefundMemberResStatus === API_RESULT.SUCCEEDED) {
      setShowSuccess(true);
    } else if (createTaxRefundMemberResStatus === API_RESULT.FAILED) {
      setShowSuccess(false);
      switch (createTaxRefundMemberError.data.responseCode) {
        case 1:
          setErrorMessageText(strings.t_tax_refund_RC_1);//+ " " + GLOBAL_BLUE_THIRD_PARTY_LINK
          setErrorMessageSubtitle("");
          break;
        case 2:
          setErrorMessageText(strings.t_tax_refund_RC_2);
          setErrorMessageSubtitle("");
          break;
        case 3:
          setErrorMessageText(strings.t_tax_refund_RC_3);
          setErrorMessageSubtitle("");
          break;
        default:
          setErrorMessageText(strings.t_tax_refund_general_error_title);
          setErrorMessageSubtitle(strings.t_tax_refund_general_error_message);
      }
    }
  }, [createTaxRefundMemberResStatus]);

  const replacePlaceholdersWithImages = (
    str: string
  ): (string | ReactNode)[] => {
    // If str is undefined, return an empty array or a fallback string
    if (!str) {
      return [""]; // Fallback to an empty string or some default text if needed
    }
    // Split the string at the placeholders and map over the parts
    const parts = str.split(/({gpay}|{apay})/g); // Regular expression to match placeholders

    return parts.map((part, index) => {
      if (part === "{gpay}") {
        return (
          <img
            key={index}
            src={images.gpay}
            alt="Android"
            className={styles.gpay}
          />
        );
      }
      if (part === "{apay}") {
        return (
          <img
            key={index}
            src={images.apay}
            alt="iOS"
            className={styles.apay}
          />
        );
      }
      return part; // Return the string part if it's not a placeholder
    });
  };

  const onDownloadAppClick = () => {
    if (isAndroid) {
      window.open(ANDROID_URL, "_blank");
    } else if (isIOS) {
      window.open(IOS_URL, "_blank");
    }
  };

  const taxRefundCondition = useMemo(() => {
    if (!taxRefundInfoRes?.data?.feCustaxRefundEligibility) return TAX_REFUND_CONDITION.NOT_ELIGIBLE;

    return getTaxRefundCondition({
      isRegistered: taxRefundInfoRes?.data?.feCustTaxRefundRegistration,
      isEligible: taxRefundInfoRes?.data?.feCustaxRefundEligibility,
    });
  }, [taxRefundInfoRes]);

  const checkIfEligibleButNotRegistered = () => {
    return taxRefundCondition === TAX_REFUND_CONDITION.ELIGIBLE_NOT_REGISTERED;
  };

  // const globalBlueTitle = useMemo(() => {
  //   if (!taxRefundInfoRes) return "";

  //   switch (taxRefundCondition) {
  //     case TAX_REFUND_CONDITION.ELIGIBLE_NOT_REGISTERED:
  //       return strings.t_global_blue_sub_title_1;
  //     case TAX_REFUND_CONDITION.ELIGIBLE_AND_REGISTERED:
  //       return strings.t_global_blue_sub_title_2;
  //     case TAX_REFUND_CONDITION.REGISTERED_NOT_ELIGIBLE:
  //       return strings.t_global_blue_sub_title_3;
  //     default:
  //       return "";
  //   }
  // }, [taxRefundCondition, strings]);

  // const acc_globalBlueTitle = useMemo(() => {
  //   if (!taxRefundInfoRes) return "";

  //   switch (taxRefundCondition) {
  //     case TAX_REFUND_CONDITION.ELIGIBLE_NOT_REGISTERED:
  //       return strings.acc_t_global_blue_sub_title_1;
  //     case TAX_REFUND_CONDITION.ELIGIBLE_AND_REGISTERED:
  //       return strings.acc_t_global_blue_sub_title_2;
  //     case TAX_REFUND_CONDITION.REGISTERED_NOT_ELIGIBLE:
  //       return strings.acc_t_global_blue_sub_title_3;
  //     default:
  //       return "";
  //   }
  // }, [taxRefundCondition, strings]);

  const getActive4digits = (
    collectCardInstruction: COLLECT_CARD_INSTRUCTION,
    acc: boolean = false
  ) => {
    // const lastCard = getCCInfoRes?.creditCards?.find(
    //   (card) => card.feCustCCStatus === CreditCardStatus.ACTIVE
    // );
    const last4Digits = fXEligibilityData.passportCard4LastDigits; //3416 bugs fix

    switch (collectCardInstruction) {
      case COLLECT_CARD_INSTRUCTION.USE_EXISTING:
        return acc
          ? strings.acc_t_summaty_footer_text1.replace("XXXX", last4Digits)
          : strings.t_summaty_footer_text1.replace("XXXX", last4Digits);
      case COLLECT_CARD_INSTRUCTION.REPLACE:
        return acc
          ? strings.acc_t_summary_card_instruction_replace.replace(
            "XXXX",
            last4Digits
          )
          : strings.t_summary_card_instruction_replace.replace(
            "XXXX",
            last4Digits
          );
      default:
        return "";
    }
  };
  const getTextBasedOnCollectCardInstruction = (acc: boolean = false) => {
    if (!fXEligibilityData) return "";
    const collectCardInstruction = fXEligibilityData.collectCardInstruction;
    switch (collectCardInstruction) {
      case COLLECT_CARD_INSTRUCTION.COLLECT:
        return acc
          ? strings.acc_t_summary_card_instruction_collect
          : strings.t_summary_card_instruction_collect;
      case COLLECT_CARD_INSTRUCTION.REPLACE:
      case COLLECT_CARD_INSTRUCTION.USE_EXISTING:
        return getActive4digits(collectCardInstruction, acc);
      default:
        return "";
    }
  };

  const showFaqPopup = () => {
    logPfsAnalyticsEvent({
      summary_global_blue_qa: true,
    } as UpdateOnboardingAnalytics);
    dispatch(
      setPopupInfo({
        show: true,
        type: POPUP_TYPE.FAQ,
        title: strings.t_tax_refaund_popup_title,
        subTitle: strings.t_tax_refaund_popup_sub_title,
        body: strings.t_tax_refaund_popup_body,
        img: images.image6,
        mobileImg: images.image6Mobile,
        okBtnText: strings.t_tax_refaund_popup_button,
        hideCloseBtn: false,
        strings: {
          questionsTitle: strings.t_tax_refauld_popup_faq,
          questionOne: strings.t_tax_refaund_popup_question_one,
          answerOne: strings.t_tax_refaund_popup_answer_one,
          questionTwo: strings.t_tax_refaund_popup_question_two,
          answerTwo: strings.t_tax_refaund_popup_answer_two,
          questionThree: strings.t_tax_refaund_popup_question_three,
          answerThree: strings.t_tax_refaund_popup_answer_three,
        },
      })
    );
  };
  return (
    <div className={styles.container}>
      <MainImageBackground
        desktopImageSrc={images.mainImage}
        mobileImageSrc={images.mainMobile}
      />
      <UICard>
        <>
          <div className={styles.redCardImageWrapper}>
            <img src={images.card} className={styles.redCardImage} alt="card" />
          </div>
          <br />
          <ScreenReaderText
            text={strings.acc_t_summary_title}
            announnceDelay={2000}
          />
          <UIText
            text={strings.t_summary_title}
            tag={TEXT_TAGS.H1}
            size={26}
            weight={600}
            style={classNames(styles.cardTitle, styles.center)}
          />
          <br />
          <UIText
            tabIndex={0}
            ariaLabel={strings.acc_t_summary_sub_title}
            text={replacePlaceholdersWithImages(strings.t_summary_sub_title)}
            size={20}
            weight={400}
            style={styles.cardSubTitleMain}
          />
          <br />
          <UIText
            tabIndex={0}
            ariaLabel={strings.acc_t_summary_sub_title_question}
            text={strings.t_summary_sub_title_question}
            size={20}
            weight={400}
            style={styles.cardSubTitleMain}
          />
          <br />
          <br />
          <Separator className={styles.separator} />
          {/* <br /> */}

          <>
            {/* <UIText
              tabIndex={0}
              ariaLabel={acc_globalBlueTitle}
              text={globalBlueTitle}
              tag={TEXT_TAGS.H2}
              size={20}
              weight={500}
              style={styles.dgeshim}
            />
            <div className={styles.globalBlueSubTitle}>
              <UIText
                tabIndex={0}
                ariaLabel={strings.acc_t_global_blue_sub_title}
                text={strings.t_global_blue_sub_title}
                size={20}
                weight={400}
                style={styles.cardSubTitle}
              />
            </div> */}


            {
              taxRefundCondition !== TAX_REFUND_CONDITION.NOT_ELIGIBLE && taxRefundCondition !== TAX_REFUND_CONDITION.ELIGIBLE_AND_REGISTERED &&
              <>
                <br />
                <br />
                <Accordion
                  index={STATE.TAX_REFAUND}
                  ariaLabel={strings.acc_t_global_blue_accordion_title}
                  title={strings.t_global_blue_accordion_title}
                  isDone={thirdSectionValid}
                  isOpen={false}
                  route={""}
                  accordionIcon={images.left}
                >
                  {!thirdSectionValid ? (
                    <TaxRefund />
                  ) : (
                    <div className={styles.successWrapper}>

                      <SuccessMessage
                        title={
                          showSuccess
                            ? strings.t_global_blue_success_title
                            : errorMessageText
                        }
                        subtitle={
                          showSuccess
                            ? strings.t_global_blue_success_subtitle
                            : errorMessageSubtitle
                        }
                        image={
                          showSuccess || createTaxRefundMemberError.data.responseCode == "1" ? images.globalBlueSuccess : images.globalBlueError
                        }
                        imageAlt="Success"
                      />
                    </div>
                  )}
                </Accordion>
              </>
            }

            {taxRefundCondition ===
              TAX_REFUND_CONDITION.ELIGIBLE_AND_REGISTERED && (
                <>
                  <br />
                  <UIText
                    tabIndex={0}
                    ariaLabel={strings.acc_t_summary_screen_qr_code_ready}
                    text={strings.t_summary_screen_qr_code_ready}
                    size={16}
                    weight={500}
                  // style={styles.link}
                  />
                </>
              )}

            {
              taxRefundCondition !== TAX_REFUND_CONDITION.NOT_ELIGIBLE &&
              <>
                <div className={styles.linkWrapper}>
                  <button
                    tabIndex={0}
                    onClick={showFaqPopup}
                    style={{
                      cursor: "pointer",
                      textDecoration: "unset",
                      border: "none",
                      background: "none",
                      padding: 0,
                    }}
                  >
                    <UIText
                      tabIndex={0}
                      ariaLabel={strings.acc_t_tax_refaund_more_info}
                      text={strings.t_tax_refaund_more_info}
                      size={18}
                      weight={400}
                      style={styles.link}
                    />
                    <img
                      src={images.link}
                      alt="arrow"
                      className={styles.leftArrow}
                    />
                  </button>
                </div>

                <Separator className={styles.separator} />
              </>
            }
          </>

          {/* {checkIfEligibleButNotRegistered() ? (
            <>
              <UIText
                tabIndex={0}
                ariaLabel={acc_globalBlueTitle}
                text={globalBlueTitle}
                tag={TEXT_TAGS.H2}
                size={20}
                weight={500}
                style={styles.dgeshim}
              />
              <div className={styles.globalBlueSubTitle}>
                <UIText
                  tabIndex={0}
                  ariaLabel={strings.acc_t_global_blue_sub_title}
                  text={strings.t_global_blue_sub_title}
                  size={20}
                  weight={400}
                  style={styles.cardSubTitle}
                />
              </div>

              <br />
              <br />
              <Accordion
                index={STATE.TAX_REFAUND}
                ariaLabel={strings.acc_t_global_blue_accordion_title}
                title={strings.t_global_blue_accordion_title}
                isDone={thirdSectionValid}
                isOpen={false}
                route={""}
                accordionIcon={images.left}
              >
                {!thirdSectionValid ? (
                  <TaxRefund />
                ) : (
                  <div className={styles.successWrapper}>
                    <SuccessMessage
                      ariaLabel={strings.acc_t_global_blue_success_title}
                      title={strings.t_global_blue_success_title}
                      subtitle={strings.t_global_blue_success_subtitle}
                      image={images.globalBlueSuccess}
                      imageAlt="Success"
                    />
                  </div>
                )}
              </Accordion>
              {taxRefundCondition ===
                TAX_REFUND_CONDITION.ELIGIBLE_AND_REGISTERED && (
                <>
                  <br />
                  <UIText
                    tabIndex={0}
                    ariaLabel={strings.acc_t_summary_screen_qr_code_ready}
                    text={strings.t_summary_screen_qr_code_ready}
                    size={16}
                    weight={500}
                    // style={styles.link}
                  />
                </>
              )}
              <div className={styles.linkWrapper}>
                <button
                  tabIndex={0}
                  onClick={showFaqPopup}
                  style={{
                    cursor: "pointer",
                    textDecoration: "unset",
                    border: "none",
                    background: "none",
                    padding: 0,
                  }}
                >
                  <UIText
                    tabIndex={0}
                    ariaLabel={strings.acc_t_tax_refaund_more_info}
                    text={strings.t_tax_refaund_more_info}
                    size={18}
                    weight={400}
                    style={styles.link}
                  />
                  <img
                    src={images.link}
                    alt="arrow"
                    className={styles.leftArrow}
                  />
                </button>
              </div>

              <Separator className={styles.separator} />
            </>
          ) : (
            <WarningBanner
              title={strings.t_tax_refaund_banner_title}
              subTitle={strings.t_tax_refaund_banner_sub_title}
            />
          )} */}

          <UIText
            tabIndex={0}
            ariaLabel={strings.acc_t_summary_dgeshim}
            text={strings.t_summary_dgeshim}
            tag={TEXT_TAGS.H2}
            size={20}
            weight={500}
            style={styles.dgeshim}
          />
          <div className={styles.cardFooter}>
            <div className={styles.footerItem}>
              <div className={styles.iconWrapper}>
                <img
                  src={images.smallCard}
                  className={styles.smallCard}
                  alt=""
                />
              </div>
              <div className={styles.labelWrapper}>
                <UIText
                  tabIndex={0}
                  ariaLabel={getTextBasedOnCollectCardInstruction(true)}
                  text={getTextBasedOnCollectCardInstruction()}
                  size={18}
                  weight={400}
                  style={classNames(styles.textRight, styles.cardSubTitle)}
                />
              </div>
            </div>
            <div className={classNames(styles.mTop, styles.footerItem)}>
              <div className={styles.iconWrapper}>
                <img
                  src={images.message}
                  className={styles.smallEmail}
                  alt=""
                />
              </div>
              <div className={styles.labelWrapper}>
                <UIText
                  tabIndex={0}
                  ariaLabel={strings.acc_t_summaty_footer_text2}
                  text={strings.t_summaty_footer_text2}
                  size={18}
                  weight={400}
                  style={classNames(styles.textRight, styles.cardSubTitle)}
                />
              </div>
            </div>
            <div className={classNames(styles.mTop, styles.footerItem)}>
              <div className={styles.iconWrapper}>
                <img
                  src={images.phoneApp}
                  className={styles.smallEmail}
                  alt=""
                />
              </div>
              <div className={styles.labelWrapper}>
                <UIText
                  tabIndex={0}
                  ariaLabel={strings.acc_t_summaty_footer_text3}
                  text={strings.t_summaty_footer_text3}
                  size={18}
                  weight={400}
                  style={classNames(styles.textRight, styles.cardSubTitle)}
                />
              </div>
            </div>
            <div className={styles.linkWrapper}>
              <a
                href={"https://www.passportcard.co.il/financial-services/limitations/"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  logPfsAnalyticsEvent({
                    summary_card_use_qa: true,
                  } as UpdateOnboardingAnalytics)
                }
              >
                <UIText
                  tabIndex={0}
                  ariaLabel={strings.acc_t_card_restrictions_button}
                  text={strings.t_card_restrictions_button}
                  size={18}
                  weight={400}
                  style={styles.link}
                />
              </a>
              <img src={images.link} alt="arrow" className={styles.leftArrow} />
            </div>
          </div>
          <Separator
            className={classNames(styles.separator, styles.marginNegative)}
          />
          <br />

          <UIText
            tabIndex={0}
            ariaLabel={
              isMobile
                ? strings.acc_t_summary_mobile_title
                : strings.acc_t_summary_body
            }
            text={
              isMobile ? strings.t_summary_mobile_title : strings.t_summary_body
            }
            tag={TEXT_TAGS.H2}
            size={22}
            weight={500}
            style={styles.cardTitle}
          />
          <UIText
            tabIndex={0}
            ariaLabel={
              isMobile
                ? strings.acc_t_summary_description_mobile
                : strings.acc_t_summary_description
            }
            text={
              isMobile
                ? strings.t_summary_description_mobile
                : strings.t_summary_description
            }
            size={20}
            weight={400}
            style={styles.cardSubTitleMain}
          />
          <div className={styles.redCardImageWrapper}>
            <QrCard
              value={qrCodeUrl || ""}
              androidUrl={ANDROID_URL}
              iosUrl={IOS_URL}
            />
          </div>
          <br />
          <br />
          <div>
            {isMobile && (
              <>
                <UIButton
                  tabIndex={0}
                  ariaLabel={strings.acc_t_summary_button}
                  title={strings.t_summary_button}
                  onSubmit={onDownloadAppClick}
                  fullWidth={isMobile ? true : false}
                />
                <br />
              </>
            )}
          </div>
        </>
      </UICard>

      {/* <div className={classNames(styles.cards, styles.cardTop)}>
        {addons.map((addon) => (
          <Addon
            key={addon.id}
            title={addon.title}
            body={addon.body}
            buttonText={addon.button}
            onSubmit={addon.onSubmit}
          />
        ))}
      </div> */}
    </div>
  );
};

export default SummaryScreen;
